import { ReactNode, useMemo } from 'react';

import { rem, Box, Button, Flex, PopoverBody, useDisclosure } from '@vimeo/bokeh';
import { ChevronDown } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import {
  trackSortByMostPopular,
  trackSortByMostRecent,
} from '../../../../tracking/tracking_engagement_qna';
import { T_MOST_POPULAR, T_NEWEST, T_OLDEST } from '../../../../translations';
import {
  EQnaTab,
  EQuestionSortType,
  EQuestionsSortBy,
  ESortOrder,
  IQnASortingRule,
  ITabSorting,
} from '../../../../types/interaction_tools';
import { createDomName } from '../../../../utils/dom/identifying';
import { BokehPopover } from '../../../bokeh';

interface IQuestionsSortingProps extends IBaseDomElementProps {
  tabId: EQnaTab;
  sorting: IQnASortingRule;
  changeSorting: (tabSorting: ITabSorting) => void;
}

export function QuestionsListSorting({
  id = createDomName('questions-list-sorting'),
  className = createDomName('questions-list-sorting'),
  tabId,
  sorting,
  changeSorting,
}: IQuestionsSortingProps) {
  const { isOpen: isMenuOpen, onClose: onCloseMenu, onToggle: onToggleMenu } = useDisclosure();

  const sortingLabel: string = {
    [EQuestionSortType.NEWEST]: T_NEWEST,
    [EQuestionSortType.OLDEST]: T_OLDEST,
    [EQuestionSortType.MOST_POPULAR]: T_MOST_POPULAR,
  }[sorting.type];

  const menuItems = useMemo(() => {
    const items = [
      {
        type: EQuestionSortType.NEWEST,
        order: ESortOrder.DESCENDING,
        copy: T_NEWEST,
        field:
          tabId === EQnaTab.ARCHIVED ? EQuestionsSortBy.ARCHIVED_TIME : EQuestionsSortBy.TIMESTAMP,
      },
      {
        type: EQuestionSortType.OLDEST,
        order: ESortOrder.ASCENDING,
        copy: T_OLDEST,
        field:
          tabId === EQnaTab.ARCHIVED ? EQuestionsSortBy.ARCHIVED_TIME : EQuestionsSortBy.TIMESTAMP,
      },
    ];

    if (tabId !== EQnaTab.PENDING) {
      items.push({
        type: EQuestionSortType.MOST_POPULAR,
        order: ESortOrder.DESCENDING,
        copy: T_MOST_POPULAR,
        field: EQuestionsSortBy.VOTES,
      });
    }

    return items;
  }, [tabId]);

  const sortingListContent: ReactNode = (
    <Box id={createDomName(id, 'list')} minWidth={rem(160)}>
      <PopoverBody>
        {menuItems.map(({ copy, field, type, order }) => (
          <Flex
            key={type}
            className={createDomName(id, 'list-item')}
            as={'span'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'start'}
            position={'relative'}
            cursor={'pointer'}
            width={'100%'}
            fontSize={'text-sm'}
            color={'text-primary'}
            borderRadius={'sm'}
            _hover={{
              backgroundColor: 'fill-component-hover',
            }}
            padding={`${rem(8)} ${rem(16)}`}
            onClick={() => {
              changeSorting({ [tabId]: { field, type, order } });
              onCloseMenu();

              // Track sorting type of qna.
              switch (type) {
                case EQuestionSortType.MOST_POPULAR:
                  trackSortByMostPopular();
                  break;

                case EQuestionSortType.NEWEST:
                  trackSortByMostRecent();
                  break;
              }
            }}
          >
            {copy}
          </Flex>
        ))}
      </PopoverBody>
    </Box>
  );

  return (
    <Flex
      id={id}
      className={className}
      alignItems={'center'}
      justifyContent={'flex-end'}
      gap={rem(8)}
    >
      <BokehPopover
        isOpen={isMenuOpen}
        inPortal={true}
        placement={'bottom-end'}
        gutter={8}
        triggerContent={
          <Button
            id={createDomName(id, 'button')}
            className={createDomName(className, 'button')}
            size={'xs'}
            variant={'minimal'}
            rightIcon={<ChevronDown />}
            onClick={onToggleMenu}
          >
            {sortingLabel}
          </Button>
        }
        content={sortingListContent}
        onClose={onCloseMenu}
      />
    </Flex>
  );
}
